'use client';

import { useMemo, useState } from 'react';
import { cityFrom } from 'lvovich';
import { distanceKm } from '@/utils/distance';
import { ICONS } from '@/components/common/gfIcons';
import { t } from '@/i18n/translations';

/* ── Расчёты времени (fallback, если нет данных в схеме) ── */

// Время на автомобиле: серпантины (+35% к прямой), 28 км/ч в городе / 55 км/ч на трассе.
// Км под временем на машине — по той же дороге (×1.35), а не по прямой: «5 км» в карточке против
// «7 км» в тексте (Гвелети, обход 01.10.2026).
const roadKm = (dist) => (dist ? dist * 1.35 : dist);

const calculateTravelTime = (dist) => {
    if (!dist || dist <= 0) return null;
    const roadKm = dist * 1.35;
    const speed = roadKm < 8 ? 28 : 55;
    return Math.ceil((roadKm / speed) * 60);
};

// Диапазон пешком: 4.2 км/ч ±20%.
const calculateWalkingTime = (dist) => {
    if (!dist || dist <= 0) return null;
    const base = (dist / 4.2) * 60;
    return { min: Math.round(base * 0.8), max: Math.round(base * 1.2) };
};

const formatTravelTime = (minutes, locale) => {
    if (!minutes || minutes <= 0) return '0';
    if (minutes < 60) return `${minutes} ${locale === 'ru' ? 'мин.' : 'min.'}`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (locale === 'ru') return mins === 0 ? `${hours} ч` : `${hours} ч ${mins} мин.`;
    return mins === 0 ? `${hours} h` : `${hours} h ${mins} min.`;
};

const formatTimeRange = (min, max, locale) => {
    if (min === max) return formatTravelTime(min, locale);
    if (max < 60) return `${min}–${max} ${locale === 'ru' ? 'мин.' : 'min.'}`;
    return `${formatTravelTime(min, locale)} – ${formatTravelTime(max, locale)}`;
};

/* ── Нормализация Sanity-данных ── */

const slugOf = (doc) => doc?.slug?.current || (typeof doc?.slug === 'string' ? doc.slug : null);

const cityNameOf = (city, locale) =>
    city?.localizedTitle || city?.localizedName || city?.label || city?.title || city?.name ||
    slugOf(city) || (locale === 'ru' ? 'города' : 'city');

const airportNameOf = (airport) => airport?.localizedTitle || airport?.title || airport?.name || '';

// «из Будвы» / «из центра Будвы» — родительный падеж (только ru)
const declineFrom = (name, locale) => {
    if (!name || locale !== 'ru') return name;
    try { return cityFrom(name); } catch { return name; }
};

/**
 * Инлайн-переключатель в тексте карточки: подчёркнутое значение + карандаш,
 * поверх — прозрачный нативный select (клик по имени или карандашу открывает его).
 */
function InlinePick({ value, options, selected, onChange, ariaLabel }) {
    if (options.length <= 1) return <span>{value}</span>;
    return (
        <span className="poi-tt-pick">
            <span className="nm">{value}</span>
            {ICONS.edit}
            <select value={selected} onChange={(e) => onChange(e.target.value)} aria-label={ariaLabel}>
                {options.map((o) => (
                    <option key={o.key} value={o.key}>{o.label}</option>
                ))}
            </select>
        </span>
    );
}

/**
 * «Время в пути» — карточки-факты в стиле ct-mosaic страниц города:
 * пешком из центра, на машине из выбираемого города, из выбираемого аэропорта.
 * Без пешей доступности карточек две (аэропортом не добиваем до трёх).
 * Время — из схемы POI (travelTimeCities/travelTimeAirports) или по расстоянию.
 */
export default function GettingThere({ poi, airportsIndex = [], citiesIndex = [], locale = 'ru' }) {
    const vp = t(locale, 'poiPageUi');
    const L = vp.travelCards;

    const poiCoords = poi?.coords;
    const originCity = poi?.gettingThereOriginCity || poi?.city;
    const originSlug = slugOf(originCity);
    const countrySlug = poi?.countrySlug || originCity?.countrySlug || originCity?.country?.slug?.current;

    // ── Города для переключателя: origin + travelTimeCities + все города страны ──
    const cityOptions = useMemo(() => {
        if (!poiCoords?.lat || !poiCoords?.lng) return [];

        // Оверрайды минут из схемы POI
        const minutesBySlug = new Map();
        (poi?.travelTimeCities || []).forEach((item) => {
            const s = slugOf(item?.city);
            if (s && item?.minutes) minutesBySlug.set(s, item.minutes);
        });

        const bySlug = new Map();
        const push = (city) => {
            const s = slugOf(city);
            if (!s || bySlug.has(s) || !city?.coords?.lat || !city?.coords?.lng) return;
            const dist = distanceKm(poiCoords, city.coords);
            if (!dist) return;
            bySlug.set(s, {
                slug: s,
                name: cityNameOf(city, locale),
                distance: dist,
                minutes: minutesBySlug.get(s) ?? calculateTravelTime(dist),
            });
        };

        if (originCity) push(originCity);
        (poi?.travelTimeCities || []).forEach((item) => item?.city && push(item.city));
        (citiesIndex || []).forEach(push);

        return [...bySlug.values()]
            .filter((c) => c.minutes)
            .sort((a, b) => a.distance - b.distance);
    }, [poi, poiCoords, originCity, citiesIndex, locale]);

    const [selectedSlug, setSelectedSlug] = useState(
        () => (cityOptions.some((c) => c.slug === originSlug) ? originSlug : cityOptions[0]?.slug)
    );
    const selectedCity = cityOptions.find((c) => c.slug === selectedSlug) || cityOptions[0];

    // ── Аэропорты для переключателя: схема POI + ручной выбор + все в стране ──
    const airportOptions = useMemo(() => {
        if (!poiCoords?.lat || !poiCoords?.lng) return [];

        const byKey = new Map();
        const push = (airport, minutesOverride) => {
            if (!airport) return;
            const key = (airport.iata || airport.code || slugOf(airport) || '').toLowerCase();
            if (!key || byKey.has(key)) return;
            const dist = airport.coords?.lat ? distanceKm(poiCoords, airport.coords) : null;
            const minutes = minutesOverride ?? (dist ? calculateTravelTime(dist) : null);
            if (!minutes) return;
            byKey.set(key, {
                key,
                iata: airport.iata || airport.code || '',
                name: airportNameOf(airport),
                minutes,
                distance: dist,
            });
        };

        (poi?.travelTimeAirports || []).forEach((item) => push(item?.airport, item?.minutes));
        (poi?.gettingThereAirports || []).forEach((ap) => push(ap));
        (airportsIndex || [])
            .filter((ap) => {
                const apCountry = ap.countrySlug || ap.country?.slug?.current || ap.country?.slug;
                return apCountry === countrySlug;
            })
            .forEach((ap) => push(ap));

        return [...byKey.values()].sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
    }, [poi, poiCoords, countrySlug, airportsIndex]);

    // По умолчанию — первый аэропорт из схемы POI, иначе ближайший
    const schemaAirportKey = (poi?.travelTimeAirports?.[0]?.airport?.iata || '').toLowerCase();
    const [selectedAirportKey, setSelectedAirportKey] = useState(
        () => (airportOptions.some((a) => a.key === schemaAirportKey) ? schemaAirportKey : airportOptions[0]?.key)
    );
    const selectedAirport = airportOptions.find((a) => a.key === selectedAirportKey) || airportOptions[0];

    // ── Пешком из центра origin-города (как раньше: схема или ≤3 км) ──
    const walkCard = useMemo(() => {
        if (!poiCoords?.lat || !originCity?.coords) return null;
        const WALK_MAX_MIN = 120;
        const schemaWalkMin = typeof poi?.travelTimeWalkingFromCenter === 'number' ? poi.travelTimeWalkingFromCenter : null;
        const distToOrigin = distanceKm(poiCoords, originCity.coords);

        let range = null;
        if (schemaWalkMin !== null && schemaWalkMin > 0 && schemaWalkMin <= WALK_MAX_MIN) {
            range = { min: Math.round(schemaWalkMin * 0.8), max: Math.round(schemaWalkMin * 1.2) };
        } else if (schemaWalkMin === null && distToOrigin && distToOrigin <= 3) {
            range = calculateWalkingTime(distToOrigin);
        }
        if (!range) return null;

        return {
            city: cityNameOf(originCity, locale),
            time: formatTimeRange(range.min, range.max, locale),
            dist: distToOrigin,
        };
    }, [poi, poiCoords, originCity, locale]);

    if (!poiCoords?.lat || !poiCoords?.lng) return null;
    if (!walkCard && !selectedCity && !selectedAirport) return null;

    const kmSub = (dist) => (dist
        ? (dist < 1 ? vp.distMeters(Math.round(dist * 1000)) : vp.distKm(Math.round(dist)))
        : null);

    const cardCount = (walkCard ? 1 : 0) + (selectedCity ? 1 : 0) + (selectedAirport ? 1 : 0);

    return (
        <>
            <div className={`poi-tt${cardCount < 3 ? ` poi-tt--${cardCount}` : ''}`}>
                {walkCard && (
                    <div className="cell">
                        <span className="k">{L.walk(declineFrom(walkCard.city, locale))}</span>
                        <span className="v">{walkCard.time}</span>
                        {kmSub(walkCard.dist) && <span className="sub">{kmSub(walkCard.dist)}</span>}
                    </div>
                )}

                {selectedCity && (
                    <div className="cell">
                        <span className="k">
                            {L.byCar}{' '}
                            <InlinePick
                                value={declineFrom(selectedCity.name, locale)}
                                options={cityOptions.map((c) => ({ key: c.slug, label: c.name }))}
                                selected={selectedCity.slug}
                                onChange={setSelectedSlug}
                                ariaLabel={L.selectCity}
                            />
                        </span>
                        <span className="v">{formatTravelTime(selectedCity.minutes, locale)}</span>
                        {kmSub(roadKm(selectedCity.distance)) && <span className="sub">{kmSub(roadKm(selectedCity.distance))}</span>}
                    </div>
                )}

                {selectedAirport && (
                    <div className="cell">
                        <span className="k">
                            {L.airport}{' '}
                            <InlinePick
                                value={[selectedAirport.name, selectedAirport.iata].filter(Boolean).join(' · ')}
                                options={airportOptions.map((a) => ({ key: a.key, label: [a.name, a.iata].filter(Boolean).join(' · ') }))}
                                selected={selectedAirport.key}
                                onChange={setSelectedAirportKey}
                                ariaLabel={L.selectAirport}
                            />
                        </span>
                        <span className="v">{formatTravelTime(selectedAirport.minutes, locale)}</span>
                        {kmSub(roadKm(selectedAirport.distance)) && <span className="sub">{kmSub(roadKm(selectedAirport.distance))}</span>}
                    </div>
                )}
            </div>
            {poi?.gettingTherePtNote && <p className="poi-tt-note">{poi.gettingTherePtNote}</p>}
        </>
    );
}
